import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, FormControlLabel, IconButton, Typography } from "@mui/material";
import Button from "../../Component/Common/Button";
import Input from "../../Component/Common/Input";
import { Button as MButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Select from "../../Component/Common/Select";
import {
  addSubscription,
  selectEditPlan,
  updateSubscription,
} from "../../store/subscription/subscription.action";
import ToggleSwitch from "../../Component/Common/ToggleSwitch";

const currencySymbol = {
  INR: "₹",
};

const CreatePlan = ({ changeTab }) => {
  const { currentSubscription } = useSelector((state) => state.subscription);
  const dispatch = useDispatch();

  // State variables for all inputs
  const [name, setName] = useState("basic");
  const [currency, setCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [hasTrial, setHasTrial] = useState(false);
  const [trialDays, setTrialDays] = useState("");
  const [billingCycle, setBillingCycle] = useState("");
  const [coursesLimit, setCoursesLimit] = useState("");
  const [membersLimit, setMembersLimit] = useState("");
  const [videosLimit, setVideosLimit] = useState("");
  const [features, setFeatures] = useState([{ description: "" }]);

  const [errors, setErrors] = useState({});

  // Handler to add new feature
  const handleAddFeature = () => {
    setFeatures([...features, { description: "" }]);
  };

  // Handler to update feature description
  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...features];
    updatedFeatures[index].description = value;
    setFeatures(updatedFeatures);
  };

  // Handler to remove feature
  const handleRemoveFeature = (index) => {
    const updatedFeatures = features.filter((_, i) => i !== index);
    setFeatures(updatedFeatures);
  };

  // Handler for form submission
  const handleSubmit = () => {
    if (
      !name ||
      !currency ||
      !amount ||
      !billingCycle ||
      !coursesLimit ||
      !membersLimit ||
      !videosLimit
    ) {
      const error = {};
      if (!name) error.name = "Name is required!";
      if (!currency) error.currency = "Currency is required!";
      if (!amount) error.amount = "Amount is required!";
      if (!billingCycle) error.billingCycle = "Billing Cycle is required!";
      if (!coursesLimit) error.coursesLimit = "Courses Limit is required!";
      if (!membersLimit) error.membersLimit = "Members Limit is required!";
      if (!videosLimit) error.videosLimit = "Videos Limit is required!";
      if (hasTrial && !Number(trialDays)) {
        error.trialDays = "Trial Days is required!";
      }

      return setErrors(error);
    }

    if (hasTrial && !Number(trialDays)) {
      return setErrors((prev) => ({
        ...prev,
        trialDays: "Trial Days is required!",
      }));
    }

    const payload = {
      name,
      pricing: {
        currency,
        amount: Number(amount),
        symbol: currencySymbol[currency],
      },
      billingCycle,
      limits: {
        maxCourses: Number(coursesLimit),
        maxMembers: Number(membersLimit),
        maxVideos: Number(videosLimit),
      },
      features: features
        .filter((f) => f.description.trim() !== "")
        .map((f) => {
          return {
            name: f.description.trim(),
            description: f.description.trim(),
          };
        }),
      hasTrial,
      trialDays: Number(trialDays),
    };
    console.log("Plan Data:", payload);

    if (currentSubscription) {
      dispatch(updateSubscription(currentSubscription._id, payload));
    } else {
      dispatch(addSubscription(payload));
    }

    changeTab();
  };

  useEffect(() => {
    if (currentSubscription) {
      setName(currentSubscription.name || "basic");
      setCurrency(currentSubscription.pricing?.currency || "");
      setAmount(currentSubscription.pricing?.amount || "");
      setHasTrial(currentSubscription.hasTrial);
      setTrialDays(currentSubscription.trialDays || "");
      setBillingCycle(currentSubscription.billingCycle || "");
      setCoursesLimit(currentSubscription.limits?.maxCourses || "");
      setMembersLimit(currentSubscription.limits?.maxMembers || "");
      setVideosLimit(currentSubscription.limits?.maxVideos || "");

      setFeatures(currentSubscription.features || [{ description: "" }]);
    }

    return () => {
      dispatch(selectEditPlan(null));
    };
  }, [currentSubscription]);

  return (
    <Box className="organizationRegister">
      <Typography variant="h6" component="h6">
        Add Plans
      </Typography>
      <hr className="border-bottom" />
      <form>
        <div className="row gy-4">
          <div className="col-12 d-flex justify-content-end align-items-center">
            <Button className={"submit"} type={"button"} onClick={handleSubmit}>
              {currentSubscription ? "Update" : "Create"}
            </Button>
          </div>
          <div className="col-12 col-lg-6">
            <div className="row row-gap-4">
              <div className="col-6">
                <Select
                  label="Plan Type"
                  placeholder="Select Plan Type"
                  options={[
                    { label: "Basic", value: "basic" },
                    { label: "Standard", value: "standard" },
                    { label: "Premium", value: "premium" },
                  ]}
                  value={name}
                  onValueChange={(value) => setName(value)}
                />
              </div>
              <div className="col-6">
                <Select
                  label="Billing Cycle"
                  placeholder="Select Cycle"
                  options={[
                    { label: "Monthly", value: "monthly" },
                    { label: "Yearly", value: "yearly" },
                  ]}
                  value={billingCycle}
                  onValueChange={(value) => {
                    setBillingCycle(value);
                    setErrors((prev) => ({
                      ...prev,
                      billingCycle: value ? "" : "Billing Cycle is required!",
                    }));
                  }}
                  errorMessage={errors.billingCycle}
                />
              </div>
              <div className="col-6">
                <Select
                  label="Currency"
                  placeholder="Select Currency"
                  options={[{ label: "INR", value: "INR" }]}
                  value={currency}
                  onValueChange={(value) => {
                    setCurrency(value);
                    setErrors((prev) => ({
                      ...prev,
                      currency: value ? "" : "Currency is required!",
                    }));
                  }}
                  errorMessage={errors.currency}
                />
              </div>
              <div className="col-6">
                <Input
                  label="Amount"
                  type="number"
                  placeholder="Enter Amount"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setErrors((prev) => ({
                      ...prev,
                      amount: e.target.value ? "" : "Amount is required!",
                    }));
                  }}
                  errorMessage={errors.amount}
                />
              </div>
              <div className="col-6">
                <FormControlLabel
                  control={
                    <ToggleSwitch
                      checked={hasTrial}
                      onChange={(event) => setHasTrial(event.target.checked)}
                    />
                  }
                  label="Enable Free Trial"
                  labelPlacement="start"
                  classes={{
                    labelPlacementStart: "ms-0 gap-2",
                    label: "fw-medium",
                  }}
                  sx={{
                    color: "#1f1f1f",
                  }}
                />
              </div>
              <div className="col-6">
                {hasTrial && (
                  <Input
                    label="Trial Days"
                    type="number"
                    placeholder="Enter Trial Days"
                    value={trialDays}
                    onChange={(e) => {
                      setTrialDays(e.target.value);
                      if (Number(e.target.value)) {
                        setErrors((prev) => ({
                          ...prev,
                          trialDays: "",
                        }));
                      } else {
                        setErrors((prev) => ({
                          ...prev,
                          trialDays: "Trial Days is required!",
                        }));
                      }
                    }}
                    errorMessage={errors.trialDays}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className="custom-input">
              <label>Limits</label>
              <div className="row row-gap-4">
                <div className="col-12 col-sm-6">
                  <Input
                    placeholder="Courses"
                    type="number"
                    value={coursesLimit}
                    onChange={(e) => {
                      setCoursesLimit(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        coursesLimit: e.target.value
                          ? ""
                          : "Courses Limit is required!",
                      }));
                    }}
                    errorMessage={errors.coursesLimit}
                  />
                </div>
                <div className="col-12 col-sm-6">
                  <Input
                    placeholder="Members"
                    type="number"
                    value={membersLimit}
                    onChange={(e) => {
                      setMembersLimit(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        membersLimit: e.target.value
                          ? ""
                          : "Members Limit is required!",
                      }));
                    }}
                    errorMessage={errors.membersLimit}
                  />
                </div>
                <div className="col-12 col-sm-6">
                  <Input
                    placeholder="Videos"
                    type="number"
                    value={videosLimit}
                    onChange={(e) => {
                      setVideosLimit(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        videosLimit: e.target.value
                          ? ""
                          : "Videos Limit is required!",
                      }));
                    }}
                    errorMessage={errors.videosLimit}
                  />
                </div>
              </div>
            </div>
            <div className="custom-input mt-4">
              <div className="d-flex justify-content-between">
                <label>Features</label>
                <MButton variant="text" onClick={handleAddFeature}>
                  Add
                </MButton>
              </div>
              {features.map((feature, index) => (
                <div key={index} className="mb-2 d-flex">
                  <Input
                    placeholder={`Description`}
                    inputContainerClass="flex-grow-1"
                    value={feature.description}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                  />
                  <IconButton onClick={() => handleRemoveFeature(index)}>
                    <CloseIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
        </div>
      </form>
    </Box>
  );
};

export default CreatePlan;
